import React from "react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { OFFER } from "@/config/offer";

const API = process.env.REACT_APP_BACKEND_URL;
const V2 = OFFER.v2;

// ── Diagnostic tracking — Meta Pixel custom events ─────────────────────
// Two lightweight, browser-only events:
//   • FL_SectionView  — fires once per section per page visit when the
//     section actually enters the viewport (IntersectionObserver).
//   • FL_CTA_Click    — fires when a purchase CTA anchor is clicked,
//     tagged with its data-cta-source ("early" | "late").
// These events are diagnostic only. They do NOT touch beginCheckout,
// the attribution bridge, PageView, Purchase, or any server-side CAPI.
// If fbq is unavailable the calls fail silently.
const fbqTrack = (event, params) => {
  try {
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      window.fbq("trackCustom", event, params);
    }
  } catch (_) {
    // never let tracking interfere with page behaviour
  }
};

// Ordered list of tracked sections. `id` is the value sent as the
// `section` parameter on FL_SectionView; `testId` matches the
// data-testid already present on the section element.
const TRACKED_SECTIONS = [
  { id: "exp1_hero",              testId: "exp1-hero" },
  { id: "exp2_reframe",           testId: "exp2-reframe" },
  { id: "exp3_framework",         testId: "exp3-framework" },
  { id: "exp3b_early_invitation", testId: "exp3b-early-invitation" },
  { id: "exp4_experience",        testId: "exp4-experience" },
  { id: "exp5_dan_story",         testId: "exp5-dan-story" },
  { id: "exp6_late_invitation",   testId: "exp6-invitation" },
  { id: "exp7_quiet_closing",     testId: "exp7-quiet-closing" },
];

// ── Attribution bridge — DO NOT MODIFY ─────────────────────────────────
// Reads Meta Pixel cookies + browser context, POSTs to /api/attribution/init,
// then redirects to the Stripe Payment Link with client_reference_id appended.
// This is the sole surface that feeds the Meta CAPI Purchase user_data.
const readCookie = (name) => {
  if (typeof document === "undefined") return undefined;
  const row = document.cookie.split("; ").find((r) => r.startsWith(name + "="));
  return row ? decodeURIComponent(row.split("=").slice(1).join("=")) : undefined;
};

const beginCheckout = async (e) => {
  if (e && e.preventDefault) e.preventDefault();
  const paymentUrl = OFFER.paymentLinkUrl;
  let targetUrl = paymentUrl;
  try {
    const fbp = readCookie("_fbp");
    let fbc = readCookie("_fbc");
    if (!fbc && typeof window !== "undefined") {
      const fbclid = new URLSearchParams(window.location.search).get("fbclid");
      if (fbclid) fbc = `fb.1.${Date.now()}.${fbclid}`;
    }
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 700);
    const resp = await fetch(`${API}/api/attribution/init`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        fbp: fbp || null,
        fbc: fbc || null,
        user_agent: (typeof navigator !== "undefined" && navigator.userAgent) || null,
        event_source_url: typeof window !== "undefined" ? window.location.href : null,
      }),
      signal: controller.signal,
    });
    clearTimeout(timeout);
    if (resp && resp.ok) {
      const data = await resp.json();
      if (data && data.attribution_id) {
        const sep = paymentUrl.includes("?") ? "&" : "?";
        targetUrl = `${paymentUrl}${sep}client_reference_id=${encodeURIComponent(data.attribution_id)}`;
      }
    }
  } catch (_) {
    // silent fallback — targetUrl stays as the raw payment link
  }
  window.location.href = targetUrl;
};

// Prose helper — sentence-per-line rhythm; paragraph breaks get extra breathing room.
const Prose = ({ text, className = "" }) => {
  const paragraphs = text.split(/\n\n+/);
  const items = [];
  paragraphs.forEach((paragraph, pi) => {
    const sentences = paragraph.trim().split(/(?<=[.?!])\s+(?=["'A-Z])/);
    sentences.forEach((sentence, si) => {
      const isFirstOfLaterParagraph = pi > 0 && si === 0;
      items.push(
        <p key={`${pi}-${si}`} className={`${className} ${isFirstOfLaterParagraph ? "mt-7" : ""}`}>
          {sentence.split("\n").map((line, k, arr) => (
            <React.Fragment key={k}>
              {line}
              {k < arr.length - 1 && <br />}
            </React.Fragment>
          ))}
        </p>
      );
    });
  });
  return items;
};

// Section wrapper — consistent generous vertical rhythm.
const Section = ({ children, eyebrow, testId, tone = "default" }) => (
  <section
    className={`border-t border-border/40 ${tone === "muted" ? "bg-muted/30" : ""}`}
    data-testid={testId}
  >
    <div className="max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-28">
      {eyebrow && (
        <p className="text-xs uppercase tracking-[0.24em] text-muted-foreground mb-8 md:mb-10">
          {eyebrow}
        </p>
      )}
      {children}
    </div>
  </section>
);

export default function Landing() {
  // ── Mount diagnostic tracking (section-view + CTA-click) ─────────────
  // Runs once on mount, cleans up on unmount. Does not modify or wrap
  // beginCheckout — CTA clicks are captured via a delegated document
  // listener on [data-cta-source] so the anchor's existing onClick
  // (beginCheckout) is entirely untouched.
  React.useEffect(() => {
    // 1) Section-view — IntersectionObserver, once per section per visit.
    // rootMargin shrinks the effective viewport bottom by 40%, so a
    // section counts as "reached" only when its top has scrolled into
    // roughly the upper 60% of the viewport. Works reliably for both
    // short and tall sections on mobile and desktop.
    let observer = null;
    const observed = [];
    if (typeof window !== "undefined" && "IntersectionObserver" in window) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const sectionId = entry.target.getAttribute("data-fl-section");
            if (!sectionId) return;
            fbqTrack("FL_SectionView", { section: sectionId });
            observer.unobserve(entry.target); // ensure once per visit
          });
        },
        { threshold: 0, rootMargin: "0px 0px -40% 0px" }
      );

      TRACKED_SECTIONS.forEach(({ id, testId }) => {
        const el = document.querySelector(`[data-testid="${testId}"]`);
        if (el) {
          el.setAttribute("data-fl-section", id);
          observer.observe(el);
          observed.push(el);
        }
      });
    }

    // 2) CTA-click — delegated document listener on [data-cta-source].
    // Fires synchronously on click, before the anchor's default
    // navigation and before beginCheckout resolves. Does not preventDefault
    // and does not modify the event in any way.
    const onDocClick = (e) => {
      const target = e.target && e.target.closest && e.target.closest("[data-cta-source]");
      if (!target) return;
      const source = target.getAttribute("data-cta-source");
      if (!source) return;
      fbqTrack("FL_CTA_Click", { cta_source: source });
    };
    if (typeof document !== "undefined") {
      // capture phase so tracking runs even if a downstream handler
      // stops propagation (defensive; today nothing does)
      document.addEventListener("click", onDocClick, true);
    }

    return () => {
      if (observer) {
        observed.forEach((el) => observer.unobserve(el));
        observer.disconnect();
      }
      if (typeof document !== "undefined") {
        document.removeEventListener("click", onDocClick, true);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground selection:bg-brand-turquoise/25">
      {/* ── Minimal nav — brand mark only, no purchase CTA (V2 §13) ────────
          Non-sticky by design: the header appears at the top of the
          experience and scrolls away naturally on all viewports. */}
      <header
        className="border-b border-border/50 bg-background"
        data-testid="site-nav"
      >
        <div className="max-w-5xl mx-auto px-6 md:px-10 h-16 flex items-center">
          <a href="#top" className="flex items-center gap-3 min-w-0" data-testid="brand-logo">
            <img src={OFFER.logoUrl} alt={OFFER.brand} className="h-8 md:h-9 w-auto" />
          </a>
        </div>
      </header>

      {/* ── EXPERIENCE 1 — Hero / Recognition ──────────────────────────────
          Two dominant questions. No CTA. No price. */}
      <section id="top" className="relative" data-testid="exp1-hero">
        <div className="max-w-3xl mx-auto px-6 md:px-10 pt-16 md:pt-32 pb-16 md:pb-28">
          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="text-xs uppercase tracking-[0.24em] text-muted-foreground"
            data-testid="exp1-eyebrow"
          >
            {V2.exp1.eyebrow}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
            className="font-serif text-4xl sm:text-5xl md:text-6xl leading-[1.08] tracking-tight mt-8 md:mt-10 text-foreground max-w-[22ch]"
            data-testid="exp1-question-1"
          >
            {V2.exp1.q1}
          </motion.h1>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 md:mt-14 space-y-5 text-[17px] md:text-lg leading-[1.75] text-foreground/85 max-w-[60ch]"
          >
            <Prose text={V2.exp1.body1} />
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="font-serif text-3xl sm:text-4xl md:text-5xl leading-[1.12] tracking-tight mt-14 md:mt-20 text-brand-turquoise max-w-[22ch]"
            data-testid="exp1-question-2"
          >
            {V2.exp1.q2}
          </motion.h2>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 md:mt-12 space-y-5 text-[17px] md:text-lg leading-[1.75] text-foreground/85 max-w-[60ch]"
          >
            <Prose text={V2.exp1.body2} />
          </motion.div>
        </div>
      </section>

      {/* ── EXPERIENCE 2 — A Different Question ────────────────────────── */}
      <Section eyebrow={V2.exp2.eyebrow} testId="exp2-reframe">
        <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-[1.15] max-w-[24ch]">
          {V2.exp2.headline}
        </h2>
        <div className="mt-10 md:mt-12 space-y-5 text-[17px] md:text-lg leading-[1.75] text-foreground/85 max-w-[60ch]">
          <Prose text={V2.exp2.body} />
        </div>
        <p
          className="mt-12 md:mt-16 font-serif text-2xl md:text-4xl italic text-brand-turquoise leading-[1.2] max-w-[22ch]"
          data-testid="exp2-anchor-question"
        >
          {V2.exp2.question}
        </p>
        <div className="mt-12 md:mt-16 space-y-5 text-[17px] md:text-lg leading-[1.75] text-foreground/85 max-w-[60ch]">
          <Prose text={V2.exp2.closing} />
        </div>
      </Section>

      {/* ── EXPERIENCE 3 — The Four Levels Framework ─────────────────────
          Left-aligned, not a card grid, not a ladder, not a hierarchy. */}
      <Section eyebrow={V2.exp3.eyebrow} testId="exp3-framework" tone="muted">
        <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-[1.15] max-w-[22ch]">
          {V2.exp3.headline}
        </h2>
        <p className="mt-10 md:mt-12 text-[17px] md:text-lg leading-[1.75] text-foreground/85 max-w-[60ch]">
          {V2.exp3.intro}
        </p>

        <div className="mt-14 md:mt-20 space-y-14 md:space-y-16">
          {V2.exp3.levels.map((lvl, i) => (
            <div
              key={i}
              className="max-w-[60ch]"
              data-testid={`exp3-level-${i}`}
            >
              <p className="text-[11px] md:text-xs uppercase tracking-[0.28em] text-brand-turquoise font-medium">
                {lvl.name}
              </p>
              <p className="font-serif text-2xl md:text-3xl italic text-foreground mt-3 leading-snug max-w-[26ch]">
                {lvl.essence}
              </p>
              <p className="text-[17px] md:text-lg leading-[1.75] text-foreground/85 mt-5">
                {lvl.body}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-16 md:mt-24 space-y-5 text-[17px] md:text-lg leading-[1.75] text-foreground/85 max-w-[60ch]">
          <Prose text={V2.exp3.closing} />
        </div>
      </Section>

      {/* ── EXPERIENCE 3B — Early optional purchase invitation ───────────
          Additional compact decision point placed after the framework
          is introduced and before the longer "What You'll Experience"
          section. Reuses the exact same beginCheckout attribution
          bridge as the primary invitation at Experience 6. Copy is
          used verbatim as approved. */}
      <section
        id="early-invitation"
        className="border-t border-border/40"
        data-testid="exp3b-early-invitation"
      >
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-14 md:py-20">
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-[1.15] max-w-[24ch]">
            {V2.exp3b.headline}
          </h2>
          <p className="mt-8 md:mt-10 text-[17px] md:text-lg leading-[1.75] text-foreground/85 max-w-[60ch]">
            {V2.exp3b.body}
          </p>
          <p
            className="mt-10 md:mt-12 font-serif text-lg md:text-xl text-foreground/80"
            data-testid="exp3b-price-line"
          >
            {V2.exp3b.priceLine}
          </p>
          <div className="mt-6 md:mt-8">
            <Button
              asChild
              size="lg"
              className="rounded-full bg-primary hover:bg-primary text-primary-foreground btn-lift px-8 h-12 text-base"
              data-testid="exp3b-cta-btn"
            >
              <a
                href={OFFER.paymentLinkUrl}
                onClick={beginCheckout}
                data-cta-source="early"
              >
                {V2.exp3b.cta} <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
          </div>
          <p
            className="mt-6 md:mt-8 text-sm text-muted-foreground"
            data-testid="exp3b-keep-scrolling"
          >
            {V2.exp3b.keepScrolling}
          </p>
        </div>
      </section>

      {/* ── EXPERIENCE 4 — What You'll Experience ────────────────────── */}
      <Section eyebrow={V2.exp4.eyebrow} testId="exp4-experience">
        <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-[1.15] max-w-[24ch]">
          {V2.exp4.headline}
        </h2>
        <div className="mt-10 md:mt-12 space-y-5 text-[17px] md:text-lg leading-[1.75] text-foreground/85 max-w-[60ch]">
          <Prose text={V2.exp4.body} />
        </div>

        <p className="mt-14 md:mt-20 text-xs uppercase tracking-[0.24em] text-muted-foreground">
          {V2.exp4.receiveHeadline}
        </p>
        <ul className="mt-8 space-y-8 max-w-[60ch]">
          {V2.exp4.receive.map((r, i) => (
            <li key={i} data-testid={`exp4-receive-${i}`}>
              <p className="font-serif text-xl md:text-2xl leading-snug text-foreground">
                {r.title}
              </p>
              <p className="text-[17px] md:text-lg leading-[1.75] text-foreground/85 mt-2">
                {r.body}
              </p>
            </li>
          ))}
        </ul>

        <div className="mt-14 md:mt-20 space-y-5 font-serif text-xl md:text-2xl italic text-brand-turquoise leading-relaxed max-w-[52ch]">
          <Prose text={V2.exp4.closing} />
        </div>
      </Section>

      {/* ── EXPERIENCE 5 — Why I Created the Four Levels ─────────────── */}
      <Section eyebrow={V2.exp5.eyebrow} testId="exp5-dan-story" tone="muted">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-start">
          <div className="md:col-span-4">
            <div className="relative">
              <div className="blob-shape" aria-hidden />
              <img
                src={OFFER.photoUrl}
                alt={OFFER.creatorName}
                className="relative z-10 w-full max-w-[280px] md:max-w-none aspect-[4/5] object-cover rounded-[42%_58%_46%_54%/55%_40%_60%_45%]"
                data-testid="exp5-dan-portrait"
              />
            </div>
          </div>
          <div className="md:col-span-8">
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-[1.15] max-w-[24ch]">
              {V2.exp5.headline}
            </h2>

            {/* Custom rhythm renderer — mobile-first resting points.
                Paragraphs get a touch more breathing room on mobile, and
                the first short reframing-question paragraph is gently
                lifted with a slightly larger serif treatment so the eye
                rests before moving on. Copy is untouched. */}
            <div className="mt-10 md:mt-12 max-w-[60ch]" data-testid="exp5-body">
              {(() => {
                const paragraphs = V2.exp5.body.split(/\n\n+/).map((p) => p.trim());
                // Pick the first short paragraph that ends with a question
                // as the single mid-story rest point.
                let restIdx = -1;
                for (let i = 1; i < paragraphs.length - 1; i++) {
                  if (paragraphs[i].length < 140 && paragraphs[i].includes("?")) {
                    restIdx = i;
                    break;
                  }
                }
                return paragraphs.map((paragraph, i) => {
                  if (i === restIdx) {
                    return (
                      <p
                        key={i}
                        className="mt-12 md:mt-12 font-serif text-[22px] md:text-2xl leading-[1.4] tracking-tight text-foreground/90 max-w-[46ch]"
                        data-testid={`exp5-para-${i}`}
                      >
                        {paragraph}
                      </p>
                    );
                  }
                  return (
                    <p
                      key={i}
                      className={`${
                        i === 0 ? "" : i === restIdx + 1 ? "mt-12 md:mt-10" : "mt-10 md:mt-7"
                      } text-[17px] md:text-lg leading-[1.8] md:leading-[1.75] text-foreground/85`}
                      data-testid={`exp5-para-${i}`}
                    >
                      {paragraph}
                    </p>
                  );
                });
              })()}
            </div>

            <p className="mt-12 md:mt-10 font-serif text-xl md:text-2xl text-foreground/70">
              {V2.exp5.signature}
            </p>
          </div>
        </div>
      </Section>

      {/* ── EXPERIENCE 6 — The Invitation (single purchase CTA) ────────── */}
      <section
        id="invitation"
        className="border-t border-border/40"
        data-testid="exp6-invitation"
      >
        <div className="max-w-2xl mx-auto px-6 md:px-10 py-24 md:py-40 text-center">
          <p className="text-xs uppercase tracking-[0.24em] text-muted-foreground">
            {V2.exp6.eyebrow}
          </p>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-[1.2] mt-8 md:mt-10 max-w-[22ch] mx-auto">
            {V2.exp6.headline}
          </h2>
          <div className="mt-10 md:mt-14 space-y-5 text-[17px] md:text-lg leading-[1.75] text-foreground/85 max-w-[52ch] mx-auto text-left">
            <Prose text={V2.exp6.body} />
          </div>

          <p className="mt-16 md:mt-20 font-serif text-xl md:text-2xl italic text-foreground/80">
            {V2.exp6.offerLabel}
          </p>
          <ul className="mt-8 space-y-3 text-[15px] md:text-base text-foreground/80 max-w-sm mx-auto">
            {V2.exp6.includes.map((it, i) => (
              <li
                key={i}
                className="flex items-baseline gap-3 justify-start"
                data-testid={`exp6-included-${i}`}
              >
                <span className="h-1.5 w-1.5 rounded-full bg-brand-green shrink-0 translate-y-1.5" />
                <span>{it}</span>
              </li>
            ))}
          </ul>

          <p
            className="mt-14 md:mt-16 font-serif text-5xl md:text-6xl text-primary"
            data-testid="exp6-price"
          >
            {V2.exp6.price}
          </p>
          <p className="mt-4 text-sm text-muted-foreground">{V2.exp6.priceNote}</p>

          <div className="mt-12 md:mt-14">
            <Button
              asChild
              size="lg"
              className="rounded-full bg-primary hover:bg-primary text-primary-foreground btn-lift px-8 h-12 text-base"
              data-testid="exp6-cta-btn"
            >
              <a href={OFFER.paymentLinkUrl} onClick={beginCheckout} data-cta-source="late">
                {V2.exp6.cta} <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
          </div>
          <p className="mt-8 text-sm text-muted-foreground leading-relaxed max-w-[42ch] mx-auto">
            {V2.exp6.reassurance}
          </p>
        </div>
      </section>

      {/* ── EXPERIENCE 7 — Quiet Closing ─────────────────────────────── */}
      <section
        className="border-t border-border/40 bg-muted/20"
        data-testid="exp7-quiet-closing"
      >
        <div className="max-w-2xl mx-auto px-6 md:px-10 py-24 md:py-40 text-center">
          <p className="font-serif text-2xl md:text-3xl italic text-foreground/80 leading-[1.3]">
            {V2.exp7.headline}
          </p>
          <div className="mt-10 md:mt-12 space-y-5 text-[17px] md:text-lg leading-[1.85] text-foreground/70 max-w-[46ch] mx-auto">
            <Prose text={V2.exp7.body} />
          </div>
        </div>
      </section>

      <footer className="border-t border-border/40">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-8 flex flex-wrap justify-between gap-3 text-sm text-muted-foreground">
          <span data-testid="footer-brand">
            &copy; {new Date().getFullYear()} {OFFER.brand}
          </span>
          <span>{OFFER.tagline}</span>
        </div>
      </footer>
    </div>
  );
}
